<template>
  <Combobox
    v-model="selectedValue"
    @update:model-value="handleValueChange"
    :display-value="(user: any) => user?.display_name || ''"
    :filter-function="filterUsers"
  >
    <div class="tw-relative">
      <ComboboxAnchor>
        <ComboboxTrigger as-child>
          <Button
            role="combobox"
            variant="secondary"
            :class="triggerClass"
            class="tw-flex tw-justify-between tw-w-full"
          >
            {{ placeholder }}
            <ChevronDown class="tw-w-4 tw-h-4 tw-shrink-0 tw-opacity-50" />
          </Button>
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxList
        position="popper"
        :class="contentClass"
        class="tw-max-h-60 tw-overflow-y-auto tw-w-full tw-min-w-[var(--radix-combobox-trigger-width)] tw-p-1 tw-z-50"
      >
        <div class="tw-p-2">
          <ComboboxInput
            placeholder="Search users..."
            class="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
          />
        </div>

        <ComboboxEmpty class="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500">
          No users found.
        </ComboboxEmpty>

        <ComboboxItem
          v-for="user in users"
          :key="user.id"
          :value="user"
          class="tw-relative tw-flex tw-cursor-default tw-select-none tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-outline-none data-[highlighted]:tw-bg-accent data-[highlighted]:tw-text-accent-foreground hover:tw-bg-gray-100"
        >
          {{ user.display_name }} ({{ user.email }})
        </ComboboxItem>
      </ComboboxList>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { ChevronDown } from 'lucide-vue-next';
import { ComboboxTrigger } from 'reka-ui';
import { ref } from 'vue';

interface User {
  id: string | number;
  display_name: string;
  email: string;
}

interface Props {
  users: User[];
  placeholder?: string;
  triggerClass?: string;
  contentClass?: string;
}

withDefaults(defineProps<Props>(), {
  placeholder: 'Add user',
  triggerClass:
    'tw-w-40 tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
  contentClass: '',
});

const emit = defineEmits<{
  select: [userId: string | number];
}>();

const filterUsers = (users: User[], searchTerm: string) => {
  const term = searchTerm.toLowerCase();
  return users.filter(
    user =>
      user.display_name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term)
  );
};

const selectedValue = ref<User | null>(null);

const handleValueChange = (user: any) => {
  if (user) {
    emit('select', user.id);
    // Reset selection after emitting to allow reselecting
    setTimeout(() => {
      selectedValue.value = null;
    }, 100);
  }
};
</script>
